// components/common/ProjectGrid/index.tsx
// Hiển thị danh sách projects + animation khi scroll

import { useEffect, useRef } from 'react'
import ProjectCard from '../ProjectCard'
import { Loader } from '../../ui'
import type { ProjectDetail } from '../../../features/projects/types'

interface ProjectGridProps {
  projects: ProjectDetail[]
  loading?: boolean
  error?: string | null
  className?: string
  emptyMessage?: string
}

const ProjectGrid = ({
  projects,
  loading = false,
  error = null,
  className = '',
  emptyMessage,
}: ProjectGridProps) => {
  const cardRefs = useRef<(HTMLElement | null)[]>([])

  // Scroll-reveal animation với IntersectionObserver
  useEffect(() => {
    const observers: IntersectionObserver[] = []

    const timer = setTimeout(() => {
      cardRefs.current.forEach((card) => {
        if (!card) return

        const rect = card.getBoundingClientRect()
        const isVisible = rect.top < window.innerHeight + 300

        if (isVisible) {
          card.classList.add('card-visible')
        } else {
          const observer = new IntersectionObserver(
            ([entry]) => {
              if (entry.isIntersecting) {
                entry.target.classList.add('card-visible')
                observer.unobserve(entry.target)
              }
            },
            { threshold: 0.05, rootMargin: '200px 0px -50px 0px' }
          )
          observer.observe(card)
          observers.push(observer)
        }
      })
    }, 50)

    return () => {
      clearTimeout(timer)
      observers.forEach((obs) => obs.disconnect())
    }
  }, [projects])

  if (loading) {
    return <Loader text="Đang tải danh sách dự án..." />
  }

  if (error) {
    return (
      <div className="error-container">
        <i className="fas fa-exclamation-triangle" style={{ fontSize: '2rem', marginBottom: '1rem' }} />
        <p>{error}</p>
      </div>
    )
  }

  if (projects.length === 0) {
    return (
      <div className="empty-container" style={{ textAlign: 'center', padding: '3rem 1rem' }}>
        <i className="fas fa-search" style={{ fontSize: '3rem', marginBottom: '1rem', color: 'var(--primary-gold)', opacity: 0.5 }} />
        <p style={{ fontSize: '1.1rem', color: 'var(--text-muted)' }}>
          {emptyMessage || 'Không có dự án nào để hiển thị'}
        </p>
      </div>
    )
  }

  return (
    <div className={`projects-grid ${className}`}>
      {projects.map((project, index) => (
        <div
          key={project.id}
          ref={(el) => { cardRefs.current[index] = el }}
        >
          <ProjectCard project={project} index={index} />
        </div>
      ))}
    </div>
  )
}

export default ProjectGrid
